import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { PLATFORMS, getPlatformUrl } from '../../constants';
import { authApi } from '../../services/authApi';
import './ConnectedPlatforms.css';

const VERIFIED_PLATFORM_LINKING_ENABLED =
  String(process.env.REACT_APP_ENABLE_VERIFIED_PLATFORM_LINKING).toLowerCase() === 'true';

/**
 * Connected platforms section with pills and account list.
 *
 * Shows platform badges for connected accounts and allows
 * adding new platform connections.
 *
 * @param {Object} props
 * @param {Array} props.accounts - Array of { platformId, username }
 * @param {function} props.onAddAccount - Handler for adding new account
 * @param {function} props.onRemoveAccount - Handler for removing account
 * @returns {JSX.Element}
 */
function ConnectedPlatforms({ accounts, onAddAccount, onRemoveAccount, onAccountsUpdate }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [newPlatform, setNewPlatform] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [connecting, setConnecting] = useState(null);
  const [toast, setToast] = useState(null);

  const connectedPlatformIds = accounts.map((a) => a.platformId);

  useEffect(() => {
    if (!VERIFIED_PLATFORM_LINKING_ENABLED || typeof window === 'undefined') {
      return undefined;
    }

    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const error = params.get('error');

    if (!connected && !error) {
      return undefined;
    }

    if (connected === 'twitch') {
      showToast('Twitch account connected', 'success');
      onAccountsUpdate?.();
    } else if (connected === 'youtube') {
      showToast('YouTube channel connected', 'success');
      onAccountsUpdate?.();
    } else if (error === 'twitch_connect_failed') {
      showToast('Twitch connection failed. Please try again.', 'error');
    } else if (error === 'twitch_token_failed') {
      showToast('Twitch token error. Please try again.', 'error');
    } else if (error === 'youtube_connect_failed') {
      showToast('YouTube connection failed. Please try again.', 'error');
    } else if (error === 'youtube_token_failed') {
      showToast('YouTube token error. Please try again.', 'error');
    } else if (error === 'no_youtube_channel') {
      showToast('No YouTube channel was found on that Google account.', 'error');
    }

    window.history.replaceState({}, '', window.location.pathname);
    return undefined;
  }, [onAccountsUpdate]);

  const handleAdd = () => {
    if (newPlatform && newUsername.trim()) {
      onAddAccount({ platformId: newPlatform, username: newUsername.trim() });
      setNewPlatform('');
      setNewUsername('');
      setIsAdding(false);
    }
  };

  const handleCancel = () => {
    setNewPlatform('');
    setNewUsername('');
    setIsAdding(false);
  };

  const getPlatformInfo = (platformId) =>
    PLATFORMS.find((p) => p.id === platformId);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3500);
  };

  const formatPlatformUsername = (account) => {
    if (!account?.username) return '';
    if (account.platformId === 'youtube' && !String(account.username).startsWith('channel/')) {
      return `@${account.username}`;
    }
    if (account.platformId === 'tiktok' && !String(account.username).startsWith('@')) {
      return `@${account.username}`;
    }
    if (account.platformId === 'twitch' && !String(account.username).startsWith('@')) {
      return `@${account.username}`;
    }
    return account.username;
  };

  const handleVerifiedConnect = async (platformId) => {
    try {
      setConnecting(platformId);
      const data = await authApi.startPlatformConnect(platformId);
      const redirectUrl = typeof data?.redirectUrl === 'string' ? data.redirectUrl.trim() : '';
      if (!redirectUrl) {
        throw new Error('Missing redirect URL');
      }
      window.location.href = redirectUrl;
    } catch (error) {
      setConnecting(null);
      showToast(error.message || `Failed to connect ${platformId}`, 'error');
    }
  };

  if (VERIFIED_PLATFORM_LINKING_ENABLED) {
    const verifiedPlatforms = ['twitch', 'youtube']
      .map((platformId) => getPlatformInfo(platformId))
      .filter(Boolean);
    const comingSoonPlatforms = PLATFORMS.filter(
      (platform) => !['twitch', 'youtube'].includes(platform.id),
    );
    const legacyAccounts = accounts.filter(
      (account) => !['twitch', 'youtube'].includes(account.platformId),
    );

    return (
      <div className="connected-platforms connected-platforms--verified-mode">
        {toast && (
          <div className={`connected-platforms__toast connected-platforms__toast--${toast.type}`}>
            {toast.message}
          </div>
        )}

        <div className="connected-platforms__header connected-platforms__header--stacked">
          <h3 className="connected-platforms__title">Connected Platforms</h3>
          <p className="connected-platforms__subtitle">
            Link Twitch or YouTube now. More platforms are coming soon.
          </p>
        </div>

        <div className="connected-platforms__verified-grid">
          {verifiedPlatforms.map((platform) => {
            const account = accounts.find((item) => item.platformId === platform.id);
            const profileUrl = account?.profileUrl || getPlatformUrl(platform.id, account?.username);

            return (
              <div
                key={platform.id}
                className={`connected-platforms__verified-card${
                  account ? ' connected-platforms__verified-card--connected' : ''
                }`}
              >
                <div className="connected-platforms__verified-main">
                  <span
                    className="connected-platforms__verified-icon"
                    style={{ color: platform.color }}
                  >
                    {platform.icon}
                  </span>
                  <div className="connected-platforms__verified-copy">
                    <span className="connected-platforms__verified-name">{platform.name}</span>
                    {account ? (
                      <a
                        className="connected-platforms__verified-username"
                        href={profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatPlatformUsername(account)}
                      </a>
                    ) : (
                      <span className="connected-platforms__verified-hint">
                        Not connected
                      </span>
                    )}
                  </div>
                </div>

                <div className="connected-platforms__verified-actions">
                  {account ? (
                    <>
                      <span className="connected-platforms__verified-badge">Verified</span>
                      <button
                        type="button"
                        className="connected-platforms__verified-btn connected-platforms__verified-btn--disconnect"
                        onClick={() => onRemoveAccount(platform.id)}
                      >
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="connected-platforms__verified-btn connected-platforms__verified-btn--connect"
                      onClick={() => handleVerifiedConnect(platform.id)}
                      disabled={connecting === platform.id}
                    >
                      {connecting === platform.id ? 'Connecting…' : `Connect ${platform.name}`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {legacyAccounts.length > 0 && (
          <div className="connected-platforms__legacy">
            <h4 className="connected-platforms__subheading">Existing connected platforms</h4>
            <div className="connected-platforms__list">
              {legacyAccounts.map((account) => {
                const platform = getPlatformInfo(account.platformId);
                if (!platform) return null;
                const profileUrl = account.profileUrl || getPlatformUrl(account.platformId, account.username);

                return (
                  <div key={account.platformId} className="connected-platforms__account">
                    <a
                      className="connected-platforms__account-link"
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span
                        className="connected-platforms__account-icon"
                        style={{ color: platform.color }}
                      >
                        {platform.icon}
                      </span>
                      <span className="connected-platforms__account-name">
                        {formatPlatformUsername(account)}
                      </span>
                    </a>
                    <button
                      type="button"
                      className="connected-platforms__remove"
                      onClick={() => onRemoveAccount(account.platformId)}
                      aria-label={`Remove ${platform.name}`}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="connected-platforms__coming-soon-inline" aria-label="More platforms coming soon">
          <span className="connected-platforms__coming-soon-prefix">Coming soon:</span>
          <div className="connected-platforms__coming-soon-list">
            {comingSoonPlatforms.map((platform) => (
              <span
                key={platform.id}
                className="connected-platforms__coming-soon-pill"
                aria-disabled="true"
              >
                <span
                  className="connected-platforms__coming-soon-pill-icon"
                  style={{ color: platform.color }}
                >
                  {platform.icon}
                </span>
                <span className="connected-platforms__coming-soon-pill-name">{platform.name}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="connected-platforms">
      {/* Section Header */}
      <div className="connected-platforms__header">
        <h3 className="connected-platforms__title">Connected Platforms</h3>
        {accounts.length > 0 && (
          <button
            type="button"
            className="connected-platforms__manage-link"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Done' : 'Manage'}
          </button>
        )}
      </div>

      {/* Platform Pills */}
      {accounts.length > 0 && (
        <div className="connected-platforms__pills">
          {accounts.map((account) => {
            const platform = getPlatformInfo(account.platformId);
            if (!platform) return null;
            const profileUrl = account.profileUrl || getPlatformUrl(account.platformId, account.username);

            return (
              <a
                key={account.platformId}
                className="connected-platforms__pill"
                style={{ '--platform-color': platform.color }}
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="connected-platforms__pill-icon">
                  {platform.icon}
                </span>
                <span className="connected-platforms__pill-name">
                  {platform.name}
                </span>
              </a>
            );
          })}
        </div>
      )}

      {/* Empty State or Expanded Card */}
      {(isExpanded || accounts.length === 0) && (
        <div className="connected-platforms__card">
          {/* Account List */}
          {accounts.length > 0 && (
            <div className="connected-platforms__list">
              {accounts.map((account) => {
                const platform = getPlatformInfo(account.platformId);
                if (!platform) return null;
                const profileUrl = account.profileUrl || getPlatformUrl(account.platformId, account.username);

                return (
                  <div
                    key={account.platformId}
                    className="connected-platforms__account"
                  >
                    <a
                      className="connected-platforms__account-link"
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span
                        className="connected-platforms__account-icon"
                        style={{ color: platform.color }}
                      >
                        {platform.icon}
                      </span>
                      <span className="connected-platforms__account-name">
                        {account.username}
                      </span>
                    </a>
                    <button
                      type="button"
                      className="connected-platforms__remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveAccount(account.platformId);
                      }}
                      aria-label={`Remove ${platform.name}`}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add New */}
          {isAdding ? (
            <div className="connected-platforms__add-form">
              <select
                className="connected-platforms__select"
                value={newPlatform}
                onChange={(e) => setNewPlatform(e.target.value)}
                aria-label="Select platform"
              >
                <option value="">Select platform...</option>
                {PLATFORMS.filter(
                  (p) => !connectedPlatformIds.includes(p.id)
                ).map((platform) => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="connected-platforms__input"
                placeholder="Username"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                aria-label="Username"
              />
              <div className="connected-platforms__add-actions">
                <button
                  type="button"
                  className="connected-platforms__cancel-btn"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="connected-platforms__save-btn"
                  onClick={handleAdd}
                  disabled={!newPlatform || !newUsername.trim()}
                >
                  Add
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="connected-platforms__add-btn"
              onClick={() => setIsAdding(true)}
            >
              + Add {accounts.length > 0 ? 'more' : 'account'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

ConnectedPlatforms.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      platformId: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
    })
  ).isRequired,
  onAddAccount: PropTypes.func.isRequired,
  onRemoveAccount: PropTypes.func.isRequired,
  onAccountsUpdate: PropTypes.func,
};

ConnectedPlatforms.defaultProps = {
  onAccountsUpdate: undefined,
};

export default ConnectedPlatforms;
